// What a payload in the per-trace X-ray claims about itself: its BASIS (what
// kind of knowledge this is, and how we came to have it) and its SOURCES (the
// turns it was built from).
//
// This exists because the feed's central move is aggregation. Every read and
// edit of one path stacks into a single reconstruction that may span a dozen
// turns, and that merge — the thing that makes the reading legible — is also
// the one thing a reader cannot check by eye. So every payload carries its own
// evidence: what it is, then where each part of it came from.
//
// Pure and outside the component because these are claims about evidence, not
// layout. Getting a basis wrong means telling a reader we know something we
// don't, which is worth a test that doesn't need a DOM.

// The four kinds of knowledge a payload can be. Keeping the vocabulary this
// small is the point: a reader learns it once and it holds across every domain.
//   reconstructed — replayed from observed content; line numbers are real and
//                   every line has a history.
//   diff only     — the edit could not be placed in any observed content, so
//                   there are no line numbers and no per-line history.
//   observed      — a single payload as it was returned to the agent.
//   not captured  — the trace names this content and does not hold it: an
//                   attachment whose bytes are dropped at capture, or a write
//                   that happened inside a shell command. Distinct from the
//                   three above because there is nothing to render and the
//                   reason matters: a reader who is not told this reads an empty
//                   body as a failure of the view.
export const BASIS = {
  RECONSTRUCTED: "Reconstructed",
  DIFF_ONLY: "Diff only",
  OBSERVED: "Observed",
  NOT_CAPTURED: "Not captured",
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// How much of the file the reconstruction actually holds.
//
// `known` (lines we have content for) and `total` (the file's tracked length)
// are maintained independently by reconstructFile, and an edit that shifts the
// length can leave known ABOVE total. Pairing them blindly then prints "5 of 4
// lines known", which is not a fact about anything — so the pair is only
// claimed when it is coherent, and otherwise only the count we can stand behind
// is reported. (The underlying disagreement is reconstructFile's to resolve;
// stating a smaller true thing is the right move here either way.)
function coverage(rec) {
  const known = rec.known || 0;
  if (!known) return null;
  if (rec.lengthKnown && rec.total >= known) return `${known} of ${rec.total} lines known`;
  return `${plural(known, "line")} known`;
}

// How many of the lines shown were seen only without their surrounding
// whitespace (a search that printed `$_.Line.Trim()`): shown at all because
// nothing showed them whole, and muted in the body, but a reader skimming the
// strip should not have to hover each one to learn the indentation is not the
// file's.
function trimmedNote(rec) {
  const trimmed = (rec.rows || []).filter((r) => r.entry?.trimmed).length;
  return trimmed ? `${trimmed} seen trimmed` : null;
}

/**
 * A file member's basis strips, from its reconstruction and the ops behind it.
 * Returns one per kind of knowledge the file carries, because they can hold at
 * once: a file can be partly reconstructed, carry hunks that couldn't be
 * anchored, AND have content the trace never captured. Collapsing those into one
 * line would hide which part of the rendering the reader is looking at.
 */
export function fileBasis(rec, items) {
  const strips = [];
  const reads = (items || []).filter((it) => it.op?.kind === "read").length;
  if (rec?.rows?.length) {
    strips.push({
      label: BASIS.RECONSTRUCTED,
      detail: [reads ? `${plural(reads, "read")} observed` : "written in-window, never read", coverage(rec), trimmedNote(rec), "hover a line for its versions"]
        .filter(Boolean)
        .join(" · "),
    });
  }
  if (rec?.blind?.length) {
    strips.push({
      // Leading "also" when it sits under a reconstruction: the two strips
      // describe different parts of the same body, not a contradiction.
      label: rec.rows.length ? `Also ${BASIS.DIFF_ONLY.toLowerCase()}` : BASIS.DIFF_ONLY,
      tone: "warn",
      detail: `${plural(rec.blindEdits, "hunk")} could not be placed in observed content — shown as authored, with no line numbers and no per-line history`,
    });
  }
  for (const s of notCapturedStrips(items, Boolean(rec?.rows?.length))) strips.push(s);
  return strips;
}

/**
 * Why a file has content the trace does not hold. Both causes can hold for one
 * file (a shell script writes an image, the agent then views it), so each is
 * reported once with its own count rather than merged into a single sentence.
 */
function notCapturedStrips(items, hasBody) {
  const list = items || [];
  const views = list.filter((it) => it.op?.media);
  const shellWrites = list.filter((it) => it.op?.shell);
  const strips = [];
  const label = (i) => (hasBody || i > 0 ? `Also ${BASIS.NOT_CAPTURED.toLowerCase()}` : BASIS.NOT_CAPTURED);
  if (views.length) {
    const media = views[views.length - 1].op.media;
    strips.push({
      label: label(strips.length),
      notCaptured: true,
      detail: `${plural(views.length, "view")} of ${media.mediaType || media.form || "an attachment"}. Attachment bytes are dropped at capture, so the trace holds the size and the type and not the file.`,
    });
  }
  if (shellWrites.length) {
    strips.push({
      label: label(strips.length),
      notCaptured: true,
      detail: `${plural(shellWrites.length, "write")} ran inside a shell command. The command is captured. What it wrote is not.`,
    });
  }
  return strips;
}

/** A single returned payload's basis — one clause, per domain. */
export function observedBasis(detail) {
  return [{ label: BASIS.OBSERVED, detail: detail || null }];
}

// What one contributing op did, for its source chip. Deliberately terse: the
// chip's job is to let a reader say "that came from turn 116's edit" without
// reading a sentence.
export function sourceNote(item) {
  const op = item?.op || {};
  switch (item?.domain) {
    case "files": {
      const delta = (op.plus || 0) + (op.minus || 0) > 0 ? ` · +${op.plus || 0} −${op.minus || 0}` : "";
      const size = !delta && op.readLines ? ` · ${plural(op.readLines, "line")}` : "";
      return `${op.kind || "op"}${delta}${size}`;
    }
    case "research":
      return op.kind === "search" ? `search · ${plural(op.resultCount || 0, "result")}` : "fetch";
    case "forge":
    case "linear":
      return [op.kind, op.create ? "created" : null].filter(Boolean).join(" · ") || null;
    default:
      return op.kind || null;
  }
}

/**
 * A payload's source chips: one per contributing op, in turn order.
 *
 * Ops with no resolved position are dropped rather than rendered as an unknown
 * turn — a chip that can't navigate anywhere is worse than one fewer chip. The
 * only op kind that lands here without a position is a slash-command skill
 * invocation, which exists in the trace as text and never had a block.
 */
export function payloadSources(items) {
  return (items || [])
    .filter((it) => it?.turnIndex != null)
    .map((it) => ({ turn: it.turnIndex, note: sourceNote(it) }))
    .sort((a, b) => a.turn - b.turn);
}
